import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Paper,
  Alert,
  CircularProgress,
  Chip,
  FormHelperText,
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import axios from '../services/axios';
import Header from '../components/Header';
import Footer from '../components/Footer';

const Settings: React.FC = () => {
  const { user, updateUser } = useAuth();
  const [formData, setFormData] = useState({
    email: user?.email || '',
    businessName: user?.businessName || '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isEmailChanged, setIsEmailChanged] = useState(false);
  const [isPasswordChanged, setIsPasswordChanged] = useState(false);

  // Check if email or password has been changed
  useEffect(() => {
    setIsEmailChanged(formData.email !== user?.email);
    setIsPasswordChanged(!!formData.newPassword);
  }, [formData.email, formData.newPassword, user?.email]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // Validate passwords if they're being changed
      if (formData.newPassword) {
        if (formData.newPassword !== formData.confirmPassword) {
          throw new Error('New passwords do not match');
        }
      }
      
      // Validate that current password is provided if changing email or password
      if ((isEmailChanged || isPasswordChanged) && !formData.currentPassword) {
        throw new Error('Current password is required when changing email or password');
      }

      const response = await axios.put('/api/auth/settings', {
        email: formData.email,
        businessName: formData.businessName,
        currentPassword: formData.currentPassword,
        newPassword: formData.newPassword,
      });

      updateUser(response.data.user);
      setSuccess(response.data.message || 'Settings updated successfully');
      setFormData(prev => ({
        ...prev,
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      }));
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to update settings');
    } finally {
      setLoading(false);
    }
  };

  // Determine if current password is required
  const isCurrentPasswordRequired = isEmailChanged || isPasswordChanged;

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <Header />
      <Box sx={{ flex: 1 }}>
        <Container maxWidth="sm">
          <Box sx={{ mt: 4, mb: 4 }}>
            <Typography variant="h4" component="h1" gutterBottom>
              Account Settings
            </Typography>
            
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            
            {success && (
              <Alert severity="success" sx={{ mb: 2 }}>
                {success}
              </Alert>
            )}

            {user?.pendingEmail && (
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  Email change to <strong>{user.pendingEmail}</strong> is pending verification. 
                  Please check your current email (<strong>{user.email}</strong>) for a verification link.
                </Typography>
              </Alert>
            )}

            <Paper elevation={3} sx={{ p: 3 }}>
              <form onSubmit={handleSubmit}>
                <TextField
                  fullWidth
                  label="Email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  margin="normal"
                  required
                  InputProps={user?.pendingEmail ? {
                    endAdornment: (
                      <Chip 
                        size="small" 
                        color="warning" 
                        label="Pending Verification"
                      />
                    )
                  } : undefined}
                />
                {isEmailChanged && (
                  <FormHelperText>
                    Changing your email requires your current password for security
                  </FormHelperText>
                )}
                
                <TextField
                  fullWidth
                  label="Business Name"
                  name="businessName"
                  value={formData.businessName}
                  onChange={handleChange}
                  margin="normal"
                  required
                />
                
                <TextField
                  fullWidth
                  label={isCurrentPasswordRequired ? "Current Password (required)" : "Current Password"}
                  name="currentPassword"
                  type="password"
                  value={formData.currentPassword}
                  onChange={handleChange}
                  margin="normal"
                  required={isCurrentPasswordRequired}
                  error={isCurrentPasswordRequired && !formData.currentPassword}
                  helperText={isCurrentPasswordRequired 
                    ? "Required when changing email or password" 
                    : "Required only if changing password or email"}
                />
                
                <TextField
                  fullWidth
                  label="New Password"
                  name="newPassword"
                  type="password"
                  value={formData.newPassword}
                  onChange={handleChange}
                  margin="normal"
                  helperText="Leave blank to keep current password"
                />
                
                <TextField
                  fullWidth
                  label="Confirm New Password"
                  name="confirmPassword"
                  type="password"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  margin="normal"
                  error={!!formData.newPassword && formData.newPassword !== formData.confirmPassword}
                  helperText={formData.newPassword && formData.newPassword !== formData.confirmPassword 
                    ? "Passwords don't match" 
                    : ""}
                  required={!!formData.newPassword}
                />
                
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  fullWidth
                  sx={{ mt: 3 }}
                  disabled={loading || (formData.newPassword !== formData.confirmPassword && !!formData.newPassword)}
                >
                  {loading ? <CircularProgress size={24} /> : 'Save Changes'}
                </Button>
              </form>
            </Paper>
          </Box>
        </Container>
      </Box>
      <Footer />
    </Box>
  );
};

export default Settings; 